import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, ExternalLink, Image, DollarSign, Calendar, User, ChevronDown, ChevronRight, RotateCcw, Download } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { ExpandableText } from '../components/ExpandableText';
import { useAuth } from '../contexts/AuthContext';
import { exportTaskToPDF } from '../utils/pdfExport';

interface Task {
  id: number;
  user_id: number;
  prompt: string;
  agent_count: number;
  status: string;
  created_at: string;
  updated_at: string;
  completed_at?: string;
}

interface Product {
  id: number;
  task_id: number;
  agent_id: string;
  name: string;
  price: number;
  currency: string;
  formatted_price: string;
  url: string;
  image_url?: string;
  screenshot_path?: string;
  description?: string;
  price_range?: string;
  recommendation_reason?: string;
  created_at: string;
}

interface ResearchSource {
  id: number;
  task_id: number;
  url: string;
  title: string;
  type: string;
  summary: string;
  relevance_score: number;
  key_insights: string[];
  created_at: string;
}

interface ResearchSummary {
  id: number;
  task_id: number;
  summary: string;
  search_queries_used: string[];
  created_at: string;
}

interface MongoResearchData {
  _id?: {
    $oid: string;
  };
  taskid: number;
  timestamp: string;
  created_at?: string;
  searchqueriesused?: string[];
  search_pages?: Array<{
    url: string;
    title?: string;
    snippet?: string;
    scraped_content?: string;
    status?: string;
  }>;
  relevant_insights?: string;
  research_summary?: string;
  insights_considerations?: string[];
  sources?: Array<{
    url: string;
    page_title: string;
    relevance_score: number;
    type?: string;
    summary?: string;
  }>;
  product_recommendations?: Array<{
    name: string;
    description: string;
    source_url: string;
    recommendation_reason: string;
    price_range: string;
    availability: string;
    image_url?: string;
  }>;
  media_opportunities?: Array<{
    product_name: string;
    media_channels?: Array<{
      channel: string;
      opportunity_type: string;
      description: string;
      target_audience: string;
      expected_reach: string;
      implementation_tips: string;
    }>;
    brand_visibility_strategies?: string[];
    conversion_tactics?: string[];
  }>;
  rawgeminiresponses?: {
    collated_raw?: {
      candidates?: Array<{
        groundingmetadata?: {
          websearchqueries?: string[];
        };
      }>;
    };
  };
  finaloutput?: {
    research_summary?: string;
    sources?: Array<{
      url: string;
      page_title: string;
      relevance_score: number;
      type?: string;
      summary?: string;
    }>;
    product_recommendations?: Array<{
      name: string;
      description: string;
      source_url: string;
      recommendation_reason: string;
      price_range: string;
      availability: string;
      image_url?: string;
    }>;
    insights_considerations?: string[];
    media_opportunities?: Array<{
      product_name: string;
      media_channels?: Array<{
        channel: string;
        opportunity_type: string;
        description: string;
        target_audience: string;
        expected_reach: string;
        implementation_tips: string;
      }>;
      brand_visibility_strategies?: string[];
      conversion_tactics?: string[];
    }>;
  };
}

interface AgentLog {
  id: number;
  task_id: number;
  agent_id: string;
  action_type: string;
  action_data: any;
  timestamp: string;
}

export const AdminTaskDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [task, setTask] = useState<Task | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [logs, setLogs] = useState<AgentLog[]>([]);
  const [researchSources, setResearchSources] = useState<ResearchSource[]>([]);
  const [researchSummary, setResearchSummary] = useState<ResearchSummary | null>(null);
  const [mongoResearchData, setMongoResearchData] = useState<MongoResearchData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAgentLog, setShowAgentLog] = useState(false);
  const [isRerunning, setIsRerunning] = useState(false);

  useEffect(() => {
    if (id) {
      fetchTaskDetails();
      fetchResearchSources();
      fetchMongoResearchData();
    }
  }, [id]);

  const fetchTaskDetails = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/tasks/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setTask(data.task);
        setProducts(data.products);
        setLogs(data.logs);
      }
    } catch (error) {
      console.error('Failed to fetch task details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResearchSources = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/tasks/${id}/sources`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setResearchSources(data.sources || []);
        setResearchSummary(data.summary);
      }
    } catch (error) {
      console.error('Failed to fetch research sources:', error);
    }
  };

  const fetchMongoResearchData = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/tasks/${id}/mongo-research`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          
          if (data.success && data.data) {
            setMongoResearchData(data.data);
          }
        } else {
          const text = await response.text();
          console.error('Non-JSON response received:', text.substring(0, 200));
        }
      } else {
        console.error('MongoDB API request failed:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Failed to fetch MongoDB research data:', error);
    }
  };

  const handleRerunTask = async () => {
    if (!task) return;
    
    setIsRerunning(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/admin/tasks/${task.id}/rerun`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Refresh the task data
        await fetchTaskDetails();
        await fetchMongoResearchData();
      } else {
        console.error('Failed to rerun task:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Failed to rerun task:', error);
    } finally {
      setIsRerunning(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-foreground">Task not found</h2>
        <Button onClick={() => navigate('/dashboard')} className="mt-4">
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const handleExportPDF = () => {
    if (task && mongoResearchData) {
      exportTaskToPDF(task, mongoResearchData);
    }
  };

  const hasValidResults = task && (task.status === 'completed' || task.status === 'success') && mongoResearchData && (
    mongoResearchData.finaloutput || 
    mongoResearchData.research_summary || 
    mongoResearchData.product_recommendations || 
    mongoResearchData.media_opportunities ||
    mongoResearchData.insights_considerations
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0">
        <Button variant="outline" onClick={() => navigate('/dashboard')} className="w-full sm:w-auto">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Task Details</h1>
          <p className="text-sm sm:text-base text-muted-foreground">View task progress and results</p>
        </div>
        {hasValidResults && (
          <Button 
            variant="default" 
            onClick={handleExportPDF}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Download className="h-4 w-4 mr-2" />
            Export PDF
          </Button>
        )}
      </div>

      {/* 1. Task Overview */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Task Overview</CardTitle>
            <Button
              variant="default"
              size="sm"
              onClick={handleRerunTask}
              disabled={isRerunning}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              {isRerunning ? 'Re-running...' : 'Re-run Task'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Prompt - Full Width */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Prompt</h3>
              <p className="text-foreground">{task.prompt}</p>
            </div>
            
            {/* Info Grid - Left to Right */}
            <div className="flex flex-wrap gap-6 text-sm">
              <div className="flex items-center space-x-2">
                <span className="font-medium text-muted-foreground">Status:</span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                  {task.status.replace('_', ' ')}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-medium text-muted-foreground">Agents:</span>
                <span className="text-foreground">{task.agent_count} AI agents</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-medium text-muted-foreground">Created:</span>
                <span className="text-foreground">{formatDate(task.created_at)}</span>
              </div>
              {task.completed_at && (
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-muted-foreground">Completed:</span>
                  <span className="text-foreground">{formatDate(task.completed_at)}</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading Section for Tasks in Progress */}
      {task && (task.status === 'in_progress' || task.status === 'processing' || task.status === 'pending') && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
              Research in Progress
            </CardTitle>
            <CardDescription>
              Your research is being processed by our AI agents
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-muted-foreground">Generating search queries</span>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-muted-foreground">Researching with {task.agent_count} AI agents</span>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-purple-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-muted-foreground">Analyzing and collating results</span>
                </div>
              </div>
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Please wait...</strong> Our AI agents are working hard to provide you with comprehensive research results. This process typically takes 2-5 minutes.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 2. Research Results - MOVED TO SECOND POSITION */}
      {task && (task.status === 'completed' || task.status === 'success') && mongoResearchData && (
        mongoResearchData.finaloutput || 
        mongoResearchData.research_summary || 
        mongoResearchData.product_recommendations || 
        mongoResearchData.media_opportunities ||
        mongoResearchData.insights_considerations
      ) && (
        <Card>
          <CardHeader>
            <CardTitle>Research Results</CardTitle>
            <CardDescription>
              Comprehensive research analysis with structured recommendations
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Research Summary from Final Output */}
            {(mongoResearchData.finaloutput?.research_summary || mongoResearchData.research_summary) && (
              <div className="mb-6 p-4 bg-green-50 rounded-lg">
                <h4 className="font-medium text-green-900 mb-2">Research Summary</h4>
                <div className="text-green-800 text-sm prose prose-sm max-w-none">
                  <ReactMarkdown>{mongoResearchData.finaloutput?.research_summary || mongoResearchData.research_summary}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Product Recommendations from Final Output - WITHOUT IMAGES */}
            {((mongoResearchData.finaloutput?.product_recommendations && mongoResearchData.finaloutput.product_recommendations.length > 0) || (mongoResearchData.product_recommendations && mongoResearchData.product_recommendations.length > 0)) && (
              <div className="mb-6">
                <h4 className="font-medium text-foreground mb-4">Product Recommendations</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(mongoResearchData.finaloutput?.product_recommendations || mongoResearchData.product_recommendations || []).map((product, index) => (
                    <Card key={index} className="overflow-hidden">
                      <CardContent className="p-4">
                        <h5 className="font-medium text-foreground mb-2 line-clamp-2">
                          {product.name}
                        </h5>
                        {product.description && (
                          <ExpandableText 
                            text={product.description} 
                            maxLength={100}
                            className="mb-3"
                          />
                        )}
                        {product.recommendation_reason && (
                          <div className="mb-3 p-2 bg-blue-50 rounded-lg">
                            <p className="text-xs text-blue-800 font-medium mb-1">Why Recommended:</p>
                            <ExpandableText 
                              text={product.recommendation_reason} 
                              maxLength={80}
                              className="text-blue-700"
                            />
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col">
                            <span className="text-lg font-semibold text-green-600">
                              {product.price_range}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {product.availability}
                            </span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (product.source_url && product.source_url.trim() !== '') {
                                const url = product.source_url.startsWith('http') ? product.source_url : `https://${product.source_url}`;
                                window.open(url, '_blank', 'noopener,noreferrer');
                              } else {
                                const searchQuery = encodeURIComponent(product.name);
                                window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank', 'noopener,noreferrer');
                              }
                            }}
                            disabled={!product.source_url && !product.name}
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            {product.source_url ? 'View' : 'Search'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Media Opportunities from Final Output */}
            {((mongoResearchData.finaloutput?.media_opportunities && mongoResearchData.finaloutput.media_opportunities.length > 0) || (mongoResearchData.media_opportunities && mongoResearchData.media_opportunities.length > 0)) && (
              <div className="mb-6">
                <h4 className="font-medium text-foreground mb-4">Media Opportunities</h4>
                <div className="space-y-4">
                  {(mongoResearchData.finaloutput?.media_opportunities || mongoResearchData.media_opportunities || []).map((opportunity, index) => (
                    <Card key={index} className="p-4">
                      <h5 className="font-medium text-foreground mb-3">{opportunity.product_name}</h5>
                      
                      {/* Media Channels */}
                      {opportunity.media_channels && opportunity.media_channels.length > 0 && (
                        <div className="mb-4">
                          <h6 className="text-sm font-medium text-foreground mb-2">Media Channels</h6>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {opportunity.media_channels.map((channel, channelIndex) => (
                              <div key={channelIndex} className="p-3 bg-muted rounded-lg">
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-1">
                                  <span className="font-medium text-foreground">{channel.channel}</span>
                                  <span className="text-xs text-muted-foreground">{channel.opportunity_type}</span>
                                </div>
                                <p className="text-sm text-muted-foreground mb-2">{channel.description}</p>
                                <div className="text-xs text-muted-foreground">
                                  <p><strong>Target:</strong> {channel.target_audience}</p>
                                  <p><strong>Reach:</strong> {channel.expected_reach}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Brand Visibility Strategies */}
                      {opportunity.brand_visibility_strategies && opportunity.brand_visibility_strategies.length > 0 && (
                        <div className="mb-4">
                          <h6 className="text-sm font-medium text-foreground mb-2">Brand Visibility Strategies</h6>
                          <ul className="list-disc list-inside space-y-1">
                            {opportunity.brand_visibility_strategies.map((strategy, strategyIndex) => (
                              <li key={strategyIndex} className="text-sm text-muted-foreground">
                                {strategy}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Conversion Tactics */}
                      {opportunity.conversion_tactics && opportunity.conversion_tactics.length > 0 && (
                        <div>
                          <h6 className="text-sm font-medium text-foreground mb-2">Conversion Tactics</h6>
                          <ul className="list-disc list-inside space-y-1">
                            {opportunity.conversion_tactics.map((tactic, tacticIndex) => (
                              <li key={tacticIndex} className="text-sm text-muted-foreground">
                                {tactic}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Insights & Considerations from Final Output */}
            {((mongoResearchData.finaloutput?.insights_considerations && mongoResearchData.finaloutput.insights_considerations.length > 0) || (mongoResearchData.insights_considerations && mongoResearchData.insights_considerations.length > 0)) && (
              <div>
                <h4 className="font-medium text-foreground mb-4">Insights & Considerations</h4>
                <div className="space-y-3">
                  {(mongoResearchData.finaloutput?.insights_considerations || mongoResearchData.insights_considerations || []).map((insight, index) => (
                    <div key={index} className="border-l-4 border-green-500 pl-4 py-2">
                      <div className="prose prose-sm max-w-none">
                        <ReactMarkdown>{insight}</ReactMarkdown>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* AI Research Disclaimer */}
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-amber-800">
                    <strong>AI Research Disclaimer:</strong> This research was conducted by artificial intelligence and may contain inaccuracies or outdated information. Please verify important findings and consult additional sources before making decisions based on these results.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 3. Search Queries - MOVED TO THIRD POSITION */}
      {task && (task.status === 'completed' || task.status === 'success') && mongoResearchData && (
        (mongoResearchData.searchqueriesused && Array.isArray(mongoResearchData.searchqueriesused) && mongoResearchData.searchqueriesused.length > 0) ||
        (mongoResearchData.rawgeminiresponses?.collated_raw?.candidates && 
         mongoResearchData.rawgeminiresponses.collated_raw.candidates.some(candidate => 
           candidate.groundingmetadata?.websearchqueries && candidate.groundingmetadata.websearchqueries.length > 0
         ))
      ) && (
        <Card>
          <CardHeader>
            <CardTitle>Search Queries</CardTitle>
            <CardDescription>
              AI-generated search queries used during research
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* AI-Generated Search Queries */}
              {mongoResearchData.searchqueriesused && Array.isArray(mongoResearchData.searchqueriesused) && mongoResearchData.searchqueriesused.length > 0 && (
                <div>
                  <h4 className="font-medium text-foreground mb-3">AI-Generated Queries</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {mongoResearchData.searchqueriesused.map((query, index) => (
                      <div 
                        key={index} 
                        className="p-3 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100 cursor-pointer transition-colors"
                        onClick={() => {
                          const searchQuery = encodeURIComponent(query);
                          window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank', 'noopener,noreferrer');
                        }}
                      >
                        <div className="flex items-start space-x-2">
                          <div className="flex-shrink-0 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-medium">
                            {index + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-blue-800 break-words">{query}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Raw Search Queries from Gemini */}
              {mongoResearchData.rawgeminiresponses?.collated_raw?.candidates && 
               mongoResearchData.rawgeminiresponses.collated_raw.candidates.some(candidate => 
                 candidate.groundingmetadata?.websearchqueries && candidate.groundingmetadata.websearchqueries.length > 0
               ) && (
                <div>
                  <h4 className="font-medium text-foreground mb-3">Raw Search Queries from Research Metadata</h4>
                  <div className="space-y-4">
                    {mongoResearchData.rawgeminiresponses.collated_raw.candidates.map((candidate, candidateIndex) => {
                      if (!candidate.groundingmetadata?.websearchqueries || candidate.groundingmetadata.websearchqueries.length === 0) {
                        return null;
                      }
                      
                      return (
                        <div key={candidateIndex} className="p-4 bg-gray-50 rounded-lg border">
                          <h5 className="font-medium text-foreground mb-3">Research Session {candidateIndex + 1}</h5>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {candidate.groundingmetadata.websearchqueries.map((query, queryIndex) => (
                              <div 
                                key={queryIndex} 
                                className="p-3 bg-white rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                                onClick={() => {
                                  const searchQuery = encodeURIComponent(query);
                                  window.open(`https://www.google.com/search?q=${searchQuery}`, '_blank', 'noopener,noreferrer');
                                }}
                              >
                                <div className="flex items-start space-x-2">
                                  <div className="flex-shrink-0 w-6 h-6 bg-gray-500 text-white rounded-full flex items-center justify-center text-xs font-medium">
                                    {queryIndex + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-800 break-words">{query}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Search Queries:</strong> These are the search queries used during the research process, including both AI-generated queries and those extracted from research metadata.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 4. Research Sources - MOVED TO FOURTH POSITION */}
      {task && (task.status === 'completed' || task.status === 'success') && mongoResearchData && mongoResearchData.finaloutput?.sources && Array.isArray(mongoResearchData.finaloutput.sources) && mongoResearchData.finaloutput.sources.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Research Sources</CardTitle>
            <CardDescription>
              Sources used during the research process
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {mongoResearchData.finaloutput.sources
                .sort((a, b) => b.relevance_score - a.relevance_score)
                .map((source, index) => (
                <div key={index} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-foreground mb-2 line-clamp-2">
                        {source.page_title}
                      </h4>
                      {source.summary && (
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-3">
                          {source.summary}
                        </p>
                      )}
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const url = source.url.startsWith('http') ? source.url : `https://${source.url}`;
                            window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          View Source
                        </Button>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2 ml-4">
                      <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {Math.round(source.relevance_score * 100)}% Relevant
                      </div>
                      {source.type && (
                        <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {source.type.charAt(0).toUpperCase() + source.type.slice(1)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>Research Sources:</strong> These are the web sources that were analyzed during the research process to provide comprehensive and up-to-date information.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 5. Agent Activity Log - KEPT AT BOTTOM (Admin Only) */}
      {isAdmin && logs && logs.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Agent Activity Log</CardTitle>
                <CardDescription>
                  Detailed log of agent actions and decisions (Admin Only)
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAgentLog(!showAgentLog)}
                className="flex items-center space-x-2"
              >
                {showAgentLog ? (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    <span>Hide</span>
                  </>
                ) : (
                  <>
                    <ChevronRight className="h-4 w-4" />
                    <span>Show</span>
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          {showAgentLog && (
            <CardContent>
              <div className="space-y-4">
                {logs.map((log) => (
                  <div key={log.id} className="border-l-4 border-blue-500 pl-4 py-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">
                          Agent {log.agent_id.substring(0, 8)}
                        </span>
                        <span className="text-sm text-muted-foreground">
                          {log.action_type.replace('_', ' ')}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(log.timestamp)}
                      </span>
                    </div>
                    {log.action_data && Object.keys(log.action_data).length > 0 && (
                      <div className="mt-2 text-sm text-muted-foreground">
                        <pre className="whitespace-pre-wrap bg-muted p-2 rounded text-xs">
                          {JSON.stringify(log.action_data, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};

export default AdminTaskDetail;
